// pa-material.ts — Material PA of IN-HOUSE STOCK (UPZ) materials (book 4.6 §18).
//
// When UPSIZE buys the material and supplies it to the workshop, a line's PA SPLITS in two: workshop PA
// (printing + cutting ONLY) + material PA (supplier). Locked rule: the workshop PA of a UPZ line NEVER
// contains a material share (anti-double-counting); the margin is computed on the SUM.
//
// The material PA is built from three values: GROSS €/m² (supplier purchase price, prefilled from the
// reference data, editable, MISSING when absent — never a silent zero) → waste COEFF % (web width/cutting
// loss, defaulted by material NATURE: flexible 30 %, rigid 50 %, none 0 %) → NET €/m² = gross × (1 + coeff).
// The NET is what enters the margin. Nature lives in the DATA (category attribute); defaults in params.

import { MATERIAL_REFS, HISTORICAL_TWINS, engineSettings } from "../../data";
import { DEFAULT_WASTE_COEFF } from "../../data/calibration";
import { type QuoteLine, num, round2, isFilled, findMat } from "../core/core";

/** Waste nature of a material, which drives the default waste coefficient. */
export type MaterialNature = "flexible" | "rigid" | "none";
/** Waste coefficient mode: the three natures (defaulted by material) + manual entry. */
export type CoeffMode = "flexible" | "rigid" | "none" | "manual";

/**
 * Material NATURE by group (reference prefix) is a category attribute read from the settings
 * (`quote_engine.material_nature`, book 4.6 §18): RIGID (50 % waste) for cutting/panels, FLEXIBLE (30 %)
 * for films/canvases/papers, NONE (0 %) for CLIENT MATERIAL (not bought by UPSIZE).
 */

/** A material whose group (reference prefix) has no nature in the settings. */
export class UnknownMaterialGroupError extends Error {
  /**
   * @param ref - The material reference at fault.
   * @param group - Its group prefix.
   */
  constructor(
    readonly ref: string,
    readonly group: string,
  ) {
    super(`Material "${ref}": group "${group}" has no nature in the settings (quote_engine.material_nature).`);
    this.name = "UnknownMaterialGroupError";
  }
}

/**
 * Group prefix of a material reference (leading letters).
 * @param ref The material reference.
 * @returns The uppercased prefix, or an empty string.
 */
function refPrefix(ref: string | undefined): string {
  const m = ref ? /^([A-Za-z]+)/.exec(ref) : null;
  return m ? m[1]!.toUpperCase() : "";
}

/**
 * Waste nature of a material group, or `null` when the reference has no group or the group is not mapped
 * in the settings. There is NO default: callers must report an unknown group.
 * @param ref The material reference.
 * @returns The material nature, or null when it cannot be told.
 */
export function materialNatureOf(ref: string | undefined): MaterialNature | null {
  const group = refPrefix(ref);
  return group ? (engineSettings().materialNature[group] ?? null) : null;
}

/**
 * Does a (non-blank) material reference belong to a group the settings do not know?
 * @param ref The material reference.
 * @returns True when the reference is filled but its group has no nature.
 */
export function hasUnknownMaterialGroup(ref: string | undefined): boolean {
  return !!ref?.trim() && materialNatureOf(ref) === null;
}

/**
 * Waste nature of a material (by group). No material yet means nothing to buy ("none"); a filled reference
 * whose group is not in the settings is an error, never a silent "rigid".
 * @param ref The material reference.
 * @returns The material nature.
 * @throws UnknownMaterialGroupError when the reference is filled but its group is not mapped.
 */
export function materialNature(ref: string | undefined): MaterialNature {
  const nature = materialNatureOf(ref);
  if (nature) return nature;
  if (!ref?.trim()) return "none";
  throw new UnknownMaterialGroupError(ref, refPrefix(ref));
}

/**
 * Is this an IN-HOUSE STOCK (UPZ) material, i.e. UPSIZE supplies it to the workshop?
 * @param ref The material reference.
 * @returns True for an in-house stock material.
 */
export function isUpz(ref: string | undefined): boolean {
  return !!findMat(ref)?.stockUpz;
}

// UPZ twins (book §18, MAT UPZ column)
// Two pairing conventions coexist: (1) the CURRENT "ref+U" convention (AD-101 ↔ AD-101U); (2) HISTORICAL
// PAIRS on adjacent refs (AD-157 ↔ AD-158…), predating the convention and held by the
// HISTORICAL_TWINS table (37 pairs, §18 invariant checked by test). The convention is consulted
// FIRST, the table SECOND. Some materials have NO twin (checkbox refused): standard materials never
// twinned, and the 3 natively UPZ stocks (AD-118, AD-135, AD-136).

/**
 * STANDARD ref → STOCK ref (historical table inverted). Built on each call: the table is injected at
 * runtime (`bindEngineData`), so it cannot be frozen at module load.
 * @returns The inverted table.
 */
function historicalBaseToUpz(): Record<string, string> {
  return Object.fromEntries(Object.entries(HISTORICAL_TWINS).map(([u, base]) => [base, u]));
}

/**
 * STOCK UPZ twin of a standard material (the "ref+U" convention, then the historical table), or null.
 * @param ref The standard material reference.
 * @returns The UPZ twin reference, or null.
 */
export function upzTwin(ref: string | undefined): string | null {
  if (!ref || isUpz(ref)) return null;
  const twin = `${ref}U`;
  if (findMat(twin)?.stockUpz) return twin;
  const historical = historicalBaseToUpz()[ref];
  return historical && findMat(historical)?.stockUpz ? historical : null;
}

/**
 * Standard BASE material of a stock twin (the "ref−U" convention, then the historical table), or null (native).
 * @param ref The UPZ material reference.
 * @returns The standard base reference, or null.
 */
export function upzBase(ref: string | undefined): string | null {
  if (!ref || !isUpz(ref)) return null;
  if (ref.endsWith("U")) {
    const base = ref.slice(0, -1);
    const m = findMat(base);
    if (m && !m.stockUpz) return base;
  }
  const historical = HISTORICAL_TWINS[ref];
  if (!historical) return null;
  const m = findMat(historical);
  return m && !m.stockUpz ? historical : null;
}

/**
 * Ref of the material in the REQUESTED state (`toUpz` true → UPZ stock; false → standard). Already in the
 * requested state → the ref itself (no-op). No twin in that direction → null: the checkbox is REFUSED and
 * the line does not move (never a silent swap to anything but the engraved pair).
 * @param ref The material reference.
 * @param toUpz Whether the requested state is UPZ stock.
 * @returns The material reference in the requested state, or null when there is no twin.
 */
export function swapUpz(ref: string | undefined, toUpz: boolean): string | null {
  if (!ref) return null;
  if (isUpz(ref) === toUpz) return ref;
  return toUpz ? upzTwin(ref) : upzBase(ref);
}

/**
 * GROSS material PA €/m² from the reference data (supplier purchase price), or null when absent.
 * @param ref The material reference.
 * @returns The gross PA, or null when the reference data does not carry it.
 */
export function paMatGrossRef(ref: string | undefined): number | null {
  const r = ref ? MATERIAL_REFS[ref] : undefined;
  return r != null && r.pa != null && String(r.pa) !== "" ? num(r.pa) : null;
}

/**
 * NET material PA €/m² = gross × (1 + waste coeff). Normative example: 6.00 × 1.30 = 7.80.
 * @param gross The gross PA €/m².
 * @param coeff The waste coefficient (fraction).
 * @returns The net PA €/m², rounded to the cent.
 */
export function paMatNet(gross: number, coeff: number): number {
  return round2(gross * (1 + coeff));
}

/** Material PA breakdown of a line (book 4.6 §18) — feeds the engine (margin) and the UI (3 fields). */
export interface MaterialPa {
  /** Is the material in-house stock UPZ? Otherwise all values are neutral ("—"). */
  isUpz: boolean;
  /** Material nature (flexible/rigid/none) → default coefficient mode. */
  nature: MaterialNature;
  /** Effective gross PA €/m² (manual entry wins, otherwise reference data), or null when missing. */
  gross: number | null;
  /** true when the gross value comes from a manual entry (≠ reference data) → orange. */
  grossManual: boolean;
  /** true when there is no gross value at all → line FLAGGED (never a silent net = 0). */
  grossMissing: boolean;
  /** Effective coefficient mode (nature default or override). */
  mode: CoeffMode;
  /** Effective waste coefficient (fraction 0..1). */
  coeff: number;
  /** true when the mode is the nature default (no override); false = orange (differs from default). */
  defaultCoeff: boolean;
  /** ORIGIN of the coefficient used (§27.2, tooltip): manual · material-specific · family default. */
  coeffOrigin: "manual" | "specific" | "family";
  /** NET material PA €/m² (gross × (1 + coeff)), or null when the gross value is missing. */
  net: number | null;
  /** Last update date of the supplier gross PA (database), for the §27.2 tooltip. null when unset. */
  grossDate?: string | null;
}

/**
 * INJECTED SOURCE of the material PA (material_suppliers + waste_coeff_by_family tables). Default =
 * embedded: gross from the reference data, coeff = nature default. The usual injection pattern — the
 * engine receives, it no longer decides.
 */
export interface MaterialPaSource {
  /** Gross PA €/m² per material (database). Takes precedence over the embedded reference data. */
  gross?: Readonly<Record<string, number | null | undefined>>;
  /** Material-SPECIFIC waste coeff (fraction 0..1). Absent for a material = family default. */
  materialCoeff?: Readonly<Record<string, number | null | undefined>>;
  /** Default coeff per NATURE/family (flexible/rigid/none). Absent = embedded DEFAULT_WASTE_COEFF. */
  coeffFamily?: Readonly<Record<string, number | null | undefined>>;
  /** Last update date of the supplier gross PA per material (ISO), for the §27.2 tooltip. */
  grossDateMap?: Readonly<Record<string, string | null | undefined>>;
}

/**
 * Resolves the material PA of a line. THREE-level COEFFICIENT cascade (§27): FAMILY default (nature) →
 * material-SPECIFIC (source) → MANUAL line entry (overrides everything). Two-level GROSS cascade:
 * database source → embedded reference data → manual entry (paMatBrut). No source = EMBEDDED behaviour
 * unchanged (byte-identical baseline).
 * @param l The quote line.
 * @param source The injected material PA source, if any.
 * @returns The material PA breakdown of the line.
 */
export function materialPa(l: QuoteLine, source?: MaterialPaSource): MaterialPa {
  const upz = isUpz(l.material);
  // An unmapped group is reported as a blocker by compute (see `hasUnknownMaterialGroup`): nothing is bought for it here.
  const nature = materialNatureOf(l.material) ?? "none";
  if (!upz) {
    return { isUpz: false, nature, gross: null, grossManual: false, grossMissing: false, mode: nature, coeff: 0, defaultCoeff: true, coeffOrigin: "family", net: null };
  }
  const ref = String(l.material ?? "");

  // GROSS: line entry > database source > embedded reference data
  const grossManual = isFilled(l.paMatGross);
  const sourceGross = source?.gross?.[ref];
  const gross = grossManual ? num(l.paMatGross) : sourceGross != null ? sourceGross : paMatGrossRef(l.material);
  const grossMissing = gross == null;

  // COEFFICIENT: line entry (manual) > material-specific > family default
  const manual = l.paMatCoeffMode === "manual";
  const specific = !manual ? source?.materialCoeff?.[ref] : undefined; // fraction 0..1
  const familyDefault = source?.coeffFamily?.[nature] ?? (nature === "none" ? 0 : DEFAULT_WASTE_COEFF[nature]);

  let coeff: number;
  let coeffOrigin: MaterialPa["coeffOrigin"];
  let mode: CoeffMode;
  if (manual) {
    coeff = Math.max(0, num(l.paMatCoeffPct) / 100);
    coeffOrigin = "manual";
    mode = "manual";
  } else if (specific != null) {
    coeff = Math.max(0, specific);
    coeffOrigin = "specific";
    mode = nature;
  } else {
    // A line mode override (forced nature) is still possible ahead of the family default.
    const lineMode = (l.paMatCoeffMode as CoeffMode) || nature;
    coeff = lineMode === "none" ? 0 : source?.coeffFamily?.[lineMode] ?? DEFAULT_WASTE_COEFF[lineMode as "flexible" | "rigid"] ?? familyDefault;
    coeffOrigin = "family";
    mode = lineMode;
  }
  // "Default" (no orange) = no line entry AND no material-specific value (pure family default).
  const defaultCoeff = coeffOrigin === "family" && (l.paMatCoeffMode == null || l.paMatCoeffMode === nature);
  const net = gross != null ? paMatNet(gross, coeff) : null;
  const grossDate = !grossManual ? source?.grossDateMap?.[ref] ?? null : null; // supplier date (source), not a line entry
  return { isUpz: upz, nature, gross, grossManual, grossMissing, mode, coeff, defaultCoeff, coeffOrigin, net, grossDate };
}
